import Color from "color";
import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

import { DEFAULT_COLORS } from "./constants";
import PlatformService from "../../services/platform";
import ServiceDeskService from "../../services/service-desk";
import { IPartner } from "../../services/platform/partner/base/types";
import { setActiveCommercePlatform } from "../../services/cyclic/client";
import { IMailbox } from "../../services/service-desk/mailbox/base/types";
import {
  PartnerColors,
  PartnerAppSettings,
  PartnerContextType,
  PartnerDashboardSettings,
} from "./types";

// ============== CONTEXT ==============
export const PartnerContext = createContext({} as PartnerContextType);

// ============== PROVIDER ==============
type Props = {
  children: React.ReactNode;
};
export function PartnerProvider({ children }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [partner, setPartner] = useState<IPartner | undefined>(undefined);
  const [mailbox, setMailbox] = useState<IMailbox | undefined>(undefined);
  const [, setAdminToolImpersonating] = useState(false);

  const initialize = useCallback(async () => {
    if (partner) {
      return;
    }

    const subdomain =
      process.env.GATSBY_FORCE_PARTNER_SUBDOMAIN ||
      window.location.hostname.split(".")[0];

    if (!subdomain) {
      setError("No subdomain found");
      setLoading(false);
      return;
    }

    const partners =
      await PlatformService.Partner.getPartnerBySubdomain(subdomain);

    if (partners.length === 0) {
      setError(`No partner found for "${subdomain}"`);
      setLoading(false);
      return;
    }

    const newPartner = partners[0];

    // A storefront session is one partner's subdomain, so the commerce platform
    // is set once here and the checkout client reads it. Without this every
    // checkout call would go to the default platform regardless of what the
    // partner's record says (lms-api#31).
    setActiveCommercePlatform(newPartner.commercePlatform || "CYCLIC");

    setPartner(newPartner);
    setLoading(false);
    setAdminToolImpersonating(false);
  }, [partner]);

  const adminToolImpersonatePartner = async (subdomain: string) => {
    if (!subdomain) {
      return;
    }

    const partners =
      await PlatformService.Partner.getPartnerBySubdomain(subdomain);

    if (partners.length === 0) {
      console.error("No partner found");
      return;
    }

    const newPartner = partners[0];

    // A storefront session is one partner's subdomain, so the commerce platform
    // is set once here and the checkout client reads it. Without this every
    // checkout call would go to the default platform regardless of what the
    // partner's record says (lms-api#31).
    setActiveCommercePlatform(newPartner.commercePlatform || "CYCLIC");

    setPartner(newPartner);
    setAdminToolImpersonating(true);
    setLoading(false);
  };

  const loadMailbox = useCallback(async () => {
    if (!partner) {
      return;
    }

    const mailboxResponse =
      await ServiceDeskService.Mailbox.getMailboxByPartnerId(partner.id);

    if (!mailboxResponse?.success || !mailboxResponse?.data) {
      console.warn("Failed to get mailbox for partner", partner.id);
      return;
    }

    if (
      mailboxResponse.data.length &&
      mailboxResponse.data[0] &&
      mailboxResponse.data[0].id
    ) {
      setMailbox(mailboxResponse.data[0]);
    }
  }, [partner?.id]);

  useEffect(() => {
    initialize();
  }, []);

  const colors: PartnerColors = useMemo(() => {
    const colorsObj = JSON.parse(
      partner?.colors || "{}"
    ) as Partial<PartnerColors>;
    Object.keys(colorsObj).forEach((key: any) => {
      const value = (colorsObj as any)[key];
      if (!value && value === "") {
        delete (colorsObj as any)[key];
      }
    });

    const mergedColors = {
      ...DEFAULT_COLORS,
      ...colorsObj,
    };

    // Dynamically generate color variants using Color library
    // Only generate if not explicitly set by partner
    const primary = mergedColors.primary;
    const secondary = mergedColors.secondary;

    return {
      ...mergedColors,
      primaryHover: colorsObj.primaryHover || Color(primary).darken(0.1).hex(),
      primaryActive:
        colorsObj.primaryActive || Color(primary).darken(0.2).hex(),
      primaryLight: colorsObj.primaryLight || Color(primary).lighten(0.4).hex(),
      secondaryHover:
        colorsObj.secondaryHover || Color(secondary).darken(0.1).hex(),
      secondaryActive:
        colorsObj.secondaryActive || Color(secondary).darken(0.2).hex(),
    };
  }, [partner]);

  const dashboardSettings: PartnerDashboardSettings = useMemo(() => {
    return JSON.parse(
      partner?.dashboardSettings || "{}"
    ) as PartnerDashboardSettings;
  }, [partner]);

  const appSettings: PartnerAppSettings = useMemo(() => {
    return JSON.parse(partner?.appSettings || "{}") as PartnerAppSettings;
  }, [partner?.appSettings]);

  // Inject colors as CSS custom properties for better performance and transitions
  useEffect(() => {
    if (colors && typeof document !== "undefined") {
      const root = document.documentElement;

      // Set all color CSS custom properties
      Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
    }
  }, [colors]);

  const memoizedValue: PartnerContextType = useMemo(
    () => ({
      loading,
      partner,
      error,
      colors,
      dashboardSettings,
      appSettings,
      mailbox,
      loadMailbox,
      adminToolImpersonatePartner,
    }),
    [
      error,
      appSettings,
      mailbox,
      loading,
      partner,
      dashboardSettings,
      colors,
      loadMailbox,
      adminToolImpersonatePartner,
    ]
  );
  return (
    <PartnerContext.Provider value={memoizedValue}>
      {children}
    </PartnerContext.Provider>
  );
}

export const usePartnerContext = () => {
  // ============== HOOK ==============
  const context = useContext(PartnerContext);
  if (!context)
    throw new Error(
      "usePartnerContext context must be use inside PartnerProvider"
    );
  return context;
};
